import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { isClosedByOther, listMyOffers, type Offer } from '@/lib/offers';
import { formatAmount } from '@/lib/format';


function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function SentOffersScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  function getStatusLabel(offer: Offer): { label: string; color: string } {
    if (offer.status === 'declined' && isClosedByOther(offer)) {
      return { label: t('offers.closedByOther'), color: '#F87171' };
    }
    const map: Record<string, { label: string; color: string }> = {
      draft:    { label: t('offers.status.draft'),     color: '#94A3B8' },
      sent:     { label: t('offers.status.pending'),   color: '#FBBF24' },
      accepted: { label: t('offers.status.accepted'),  color: '#4ADE80' },
      declined: { label: t('offers.status.declined'),  color: '#F87171' },
      expired:  { label: t('offers.status.expired'),   color: '#F87171' },
    };
    return map[offer.status] ?? { label: offer.status, color: colors.textMuted };
  }

  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | Offer['status']>('all');

  const load = useCallback(async () => {
    try {
      const all = await listMyOffers();
      setOffers(all.filter((o) => o.status !== 'draft'));
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const STATUS_FILTERS: { key: 'all' | Offer['status']; label: string }[] = useMemo(() => [
    { key: 'all',      label: t('offers.filter_all') },
    { key: 'sent',     label: t('offers.status.pending') },
    { key: 'accepted', label: t('offers.status.accepted') },
    { key: 'declined', label: t('offers.status.declined') },
    { key: 'expired',  label: t('offers.status.expired') },
  ], [t]);

  const visibleOffers = useMemo(
    () => (statusFilter === 'all' ? offers : offers.filter((o) => o.status === statusFilter)),
    [offers, statusFilter],
  );

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  async function onRefresh() {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }

  return (
    <View style={styles.root}>
      <View style={[styles.header, { paddingTop: insets.top + spacing.lg }]}>
        <View style={styles.headerRow}>
          <Text style={styles.title} numberOfLines={1} adjustsFontSizeToFit>{t('offers.sent_title_screen')}</Text>
          <View style={styles.headerActions}>
            <Pressable
              style={styles.templatesBtn}
              onPress={() => router.push('/(app)/offers/templates')}
              accessibilityRole="button"
              accessibilityLabel={t('offersScreens.templatesTitle')}
              hitSlop={8}
            >
              <Ionicons name="copy-outline" size={18} color={colors.text} />
            </Pressable>
            <Pressable
              style={styles.newBtn}
              onPress={() => router.push('/(app)/offers/new')}
              accessibilityRole="button"
            >
              <Ionicons name="add" size={18} color="#fff" />
              <Text style={styles.newBtnText}>{t('common.new')}</Text>
            </Pressable>
          </View>
        </View>
      </View>

      {offers.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chips}
          style={styles.chipsRow}
        >
          {STATUS_FILTERS.map((f) => {
            const selected = statusFilter === f.key;
            return (
              <Pressable
                key={f.key}
                onPress={() => setStatusFilter(f.key)}
                style={[styles.chip, selected && styles.chipSelected]}
                accessibilityRole="button"
              >
                <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{f.label}</Text>
              </Pressable>
            );
          })}
        </ScrollView>
      )}

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : offers.length === 0 ? (
        <View style={styles.center}>
          <Ionicons name="paper-plane-outline" size={48} color={colors.border} />
          <Text style={styles.emptyTitle}>{t('offers.sent_empty_title')}</Text>
          <Text style={styles.emptyText}>{t('offers.sent_empty_body')}</Text>
        </View>
      ) : visibleOffers.length === 0 ? (
        <View style={styles.center}>
          <Ionicons name="filter-outline" size={48} color={colors.border} />
          <Text style={styles.emptyTitle}>{t('offers.filter_empty')}</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.list}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
        >
          {visibleOffers.map((item) => {
            const days = daysUntil(item.expires_at);
            const badge = getStatusLabel(item);
            return (
              <Pressable
                key={item.id}
                style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
                onPress={() =>
                  router.push({ pathname: '/(app)/offers/[id]', params: { id: item.id } })
                }
                accessibilityRole="button"
              >
                <View style={styles.cardTop}>
                  <Text style={styles.cardTitle} numberOfLines={1}>{item.title}</Text>
                  <View style={[styles.statusBadge, { borderColor: badge.color + '55' }]}>
                    <Text style={[styles.statusText, { color: badge.color }]}>{badge.label}</Text>
                  </View>
                </View>
                {!!item.description && (
                  <Text style={styles.cardDesc} numberOfLines={2}>{item.description}</Text>
                )}
                <View style={styles.cardMeta}>
                  {item.cost != null && (
                    <View style={styles.metaItem}>
                      <Ionicons name="cash-outline" size={13} color={colors.textMuted} />
                      <Text style={styles.metaText}>{formatAmount(item.cost, item.currency)}</Text>
                    </View>
                  )}
                  {item.delivery_days != null && (
                    <View style={styles.metaItem}>
                      <Ionicons name="calendar-outline" size={13} color={colors.textMuted} />
                      <Text style={styles.metaText}>{item.delivery_days} {item.delivery_days === 1 ? t('common.day') : t('common.days')}</Text>
                    </View>
                  )}
                  {item.status === 'sent' && days !== null && days > 0 && (
                    <View style={styles.metaItem}>
                      <Ionicons
                        name="time-outline"
                        size={13}
                        color={days <= 3 ? '#F87171' : colors.textMuted}
                      />
                      <Text style={[styles.metaText, days <= 3 && { color: '#F87171' }]}>
                        {days === 1 ? `1 ${t('common.day')}` : `${days} ${t('common.days')}`}
                      </Text>
                    </View>
                  )}
                  {!!item.recipient_email && (
                    <View style={styles.metaItem}>
                      <Ionicons name="mail-outline" size={13} color={colors.textMuted} />
                      <Text style={styles.metaText} numberOfLines={1}>{item.recipient_email}</Text>
                    </View>
                  )}
                </View>
              </Pressable>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: colors.background },
    header: {
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.xxl,
      fontWeight: '800',
      marginRight: spacing.sm,
    },
    headerActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexShrink: 0 },
    templatesBtn: {
      width: 34,
      height: 34,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    newBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: colors.primary,
      borderRadius: radius.md,
      paddingVertical: 8,
      paddingHorizontal: spacing.md,
    },
    newBtnText: {
      color: '#fff',
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.md,
      paddingHorizontal: spacing.xl,
    },
    emptyTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
      textAlign: 'center',
    },
    emptyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      lineHeight: 22,
    },
    chipsRow: {
      flexGrow: 0,
      flexShrink: 0,
      marginBottom: spacing.sm,
    },
    chips: {
      gap: spacing.sm,
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.xs,
    },
    chip: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 6,
      paddingHorizontal: spacing.md,
    },
    chipSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    chipText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    chipTextSelected: {
      color: '#fff',
    },
    list: { flex: 1 },
    listContent: {
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.xl,
      gap: spacing.sm,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
      ...shadow.card,
    },
    cardPressed: { opacity: 0.75 },
    cardTop: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    cardTitle: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    statusBadge: {
      borderRadius: radius.full,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderWidth: 1,
      backgroundColor: 'transparent',
    },
    statusText: {
      fontSize: 11,
      fontWeight: '700',
    },
    cardDesc: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    cardMeta: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.sm,
      marginTop: 2,
    },
    metaItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    metaText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
  });
}
